import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, Field, HeroPill, Input, MobileHero, MobilePage, SectionHeader } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MobileChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);

  const requirements = useMemo(() => ({
    minLength: newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasLowercase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
  }), [newPassword]);

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const formIsValid = currentPassword && Object.values(requirements).every(Boolean) && passwordsMatch;

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!formIsValid) {
      setError('Complete all fields and meet the password requirements.');
      return;
    }
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || data.error || 'Unable to change password.');
      toast.success('Password changed successfully');
      navigate('/profile');
    } catch (submitError) {
      setError(submitError.message || 'Unable to change password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Security</HeroPill>
        <h2>Change Password</h2>
        <p>Update your practitioner account password with a strong, secure replacement.</p>
      </MobileHero>

      <Card as="form" onSubmit={submit}>
        <SectionHeader><div><span>Credentials</span><h3>Password update</h3></div></SectionHeader>
        {error && <ErrorBox>{error}</ErrorBox>}
        <FormStack>
          <Field>Current password<PasswordWrap><Input value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} type={visible ? 'text' : 'password'} autoComplete="current-password" /><button type="button" onClick={() => setVisible(!visible)}>{visible ? 'Hide' : 'Show'}</button></PasswordWrap></Field>
          <Field>New password<PasswordWrap><Input value={newPassword} onChange={event => setNewPassword(event.target.value)} type={visible ? 'text' : 'password'} autoComplete="new-password" /><button type="button" onClick={() => setVisible(!visible)}>{visible ? 'Hide' : 'Show'}</button></PasswordWrap></Field>
          <Field>Confirm password<PasswordWrap><Input value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} type={visible ? 'text' : 'password'} autoComplete="new-password" /><button type="button" onClick={() => setVisible(!visible)}>{visible ? 'Hide' : 'Show'}</button></PasswordWrap></Field>
        </FormStack>
        <RequirementList>
          <li data-ok={requirements.minLength}>At least 8 characters</li>
          <li data-ok={requirements.hasUppercase}>One uppercase letter</li>
          <li data-ok={requirements.hasLowercase}>One lowercase letter</li>
          <li data-ok={requirements.hasNumber}>One number</li>
          <li data-ok={passwordsMatch}>Passwords match</li>
        </RequirementList>
        <Button $full disabled={submitting} type="submit">{submitting ? 'Updating…' : 'Update password'}</Button>
      </Card>
    </MobilePage>
  );
};

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PasswordWrap = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  display: flex;
  padding-right: 8px;
  input { border: 0; background: transparent; }
  button { background: transparent; border: 0; color: #0284c7; font-size: 0.76rem; font-weight: 900; padding: 0 8px; }
`;

const ErrorBox = styled.div`
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 16px;
  color: #be123c;
  font-size: 0.82rem;
  font-weight: 850;
  margin-bottom: 12px;
  padding: 12px;
`;

const RequirementList = styled.ul`
  display: grid;
  gap: 7px;
  list-style: none;
  margin: 14px 0;
  padding: 0;

  li {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 800;
  }

  li::before {
    content: '○';
    margin-right: 8px;
  }

  li[data-ok='true'] {
    color: #059669;
  }

  li[data-ok='true']::before {
    content: '●';
  }
`;

export default MobileChangePassword;
